/**
 * The `⋯` row menu — ONE component, used by both the card view
 * (`FleetDeviceCard.tsx`) and the list view (`FleetTable.tsx`'s list rows).
 * Everything here is wiring to endpoints that already exist:
 * `deviceConfigApi.setDeviceRelease` / `setDeviceChannel`, and the drawer's
 * own existing tier-3 `clear_pairing` flow for re-provisioning — this never
 * adds a second path for either.
 *
 * Custom popover only — never a native `<select>`/`confirm()`, both banned
 * platform-wide and invisible in the iOS webview. Closes on outside-click and
 * on Escape, every item is a real `<button>` (keyboard reachable), 44px
 * minimum touch targets.
 *
 * *** `pinnedVersion: null`, never `undefined` *** — `JSON.stringify` drops
 * an `undefined` key entirely, so an omitted key survives server-side as
 * whatever was already stored. `releasePin` below sends `null` explicitly;
 * do not "simplify" that to leaving the key out.
 *
 * An item that cannot apply to this row is HIDDEN, not disabled-and-empty —
 * per the brief's guard table. `provisionCode` is a pairing credential: it is
 * copied to the clipboard, never logged.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { deviceConfigApi } from '../../../services/deviceConfigApi';
import { toastService } from '../../../services/toastService';
import type { FleetRow } from './fleetRows';

const RINGS: Array<'canary' | 'pilot' | 'stable'> = ['canary', 'pilot', 'stable'];

interface MenuItem {
  key: string;
  label: string;
  onClick: () => void;
  danger?: boolean;
  /**
   * Amber, NOT rose/`danger` — `danger` reads as destructive (re-provision,
   * factory wipe). "Turn auto-update off" DEGRADES the till (it stops
   * updating itself) while otherwise looking identical to a routine item
   * like "Move to stable", so it needs to read as caution, not alarm: it is
   * per-device, instantly reversible, toasted, and leaves a visible pill.
   */
  caution?: boolean;
}

export default function FleetRowMenu({
  row,
  onChanged,
  onReprovision,
  buttonClassName,
  buttonTitle = 'More actions',
}: {
  row: FleetRow;
  /** Re-fetch the fleet after a change lands, same contract as the card's other actions. */
  onChanged: () => void;
  /**
   * Opens the drawer's EXISTING tier-3 `clear_pairing` flow — "unpairs the
   * register, re-provision needed". That IS the re-provision path; this menu
   * never issues a second command for it.
   */
  onReprovision: () => void;
  buttonClassName?: string;
  buttonTitle?: string;
}) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  /**
   * WHERE THE MENU IS DRAWN, in viewport coordinates.
   *
   * The menu is PORTALLED to `document.body` rather than positioned
   * `absolute` inside the row. The list view wraps its table in
   * `overflow-hidden` (needed for the rounded corners), and an absolutely
   * positioned child of a clipping ancestor is clipped no matter how high its
   * z-index — which is exactly how this menu came to be half-drawn and
   * unusable in list view. A portal escapes every ancestor's overflow; the
   * trade is that we must position it ourselves against the button's rect.
   */
  const [anchor, setAnchor] = useState<{ top: number; left: number; flipUp: boolean } | null>(null);

  /** Menu width must match the rendered `w-56` (14rem) so the right edge lines up. */
  const MENU_W = 224;
  /** Enough for the tallest menu we build; only used to decide flip direction. */
  const MENU_H_ESTIMATE = 300;

  useLayoutEffect(() => {
    if (!open) { setAnchor(null); return; }
    const place = () => {
      const b = buttonRef.current?.getBoundingClientRect();
      if (!b) return;
      const flipUp = b.bottom + MENU_H_ESTIMATE > window.innerHeight && b.top > MENU_H_ESTIMATE;
      // Right-aligned to the button, then clamped so it can never hang off
      // the left edge of the viewport on a narrow screen.
      const left = Math.max(8, Math.min(b.right - MENU_W, window.innerWidth - MENU_W - 8));
      setAnchor({ top: flipUp ? b.top - 4 : b.bottom + 4, left, flipUp });
    };
    place();
    // Reposition rather than close: a menu that vanishes because the page
    // scrolled a pixel is worse than one that follows its button.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: MouseEvent) => {
      const t = e.target as Node;
      // The menu is portalled OUT of `rootRef`, so an outside-click test that
      // only consulted `rootRef` would treat every click on the menu's own
      // items as "outside" and close it before the item fired.
      if (rootRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const close = () => setOpen(false);

  const copyCode = async () => {
    if (!row.provisionCode) return;
    try {
      // Never console.log(row.provisionCode) — it is a pairing credential.
      await navigator.clipboard.writeText(row.provisionCode);
      toastService.success('Provisioning code copied');
    } catch {
      toastService.error('Could not copy — copy it by hand');
    }
    close();
  };

  const withBusy = async (fn: () => Promise<void>) => {
    setBusy(true);
    try {
      await fn();
      onChanged();
    } catch (e: any) {
      toastService.error(e?.message || 'That action failed');
    } finally {
      setBusy(false);
      close();
    }
  };

  const pinToCurrent = () => withBusy(async () => {
    if (!row.deviceId || !row.deviceType || !row.appVersion) return;
    await deviceConfigApi.setDeviceRelease(row.deviceType, row.deviceId, { pinnedVersion: row.appVersion });
    toastService.success(`Pinned to ${row.appVersion}`);
  });

  const releasePin = () => withBusy(async () => {
    if (!row.deviceId || !row.deviceType) return;
    // MUST be `null`, never omitted/`undefined` — see the file-level note.
    await deviceConfigApi.setDeviceRelease(row.deviceType, row.deviceId, { pinnedVersion: null });
    toastService.success('Pin released');
  });

  const moveRing = (ring: 'canary' | 'pilot' | 'stable') => withBusy(async () => {
    if (!row.deviceId || !row.deviceType) return;
    await deviceConfigApi.setDeviceRelease(row.deviceType, row.deviceId, { releaseRing: ring });
    toastService.success(`Moved to ${ring}`);
  });

  // Task 10 — the remote off switch. Stored on the device row, handed back
  // on the device's next check-in, never delivered as a command.
  const setAutoUpdate = (autoUpdate: boolean) => withBusy(async () => {
    if (!row.deviceId || !row.deviceType) return;
    await deviceConfigApi.setDeviceAutoUpdate(row.deviceType, row.deviceId, autoUpdate);
    toastService.success(autoUpdate ? 'Auto-update turned back on' : 'Auto-update turned off');
  });

  const items: MenuItem[] = [];

  // "Turn auto-update back on" is deliberately the FIRST item when the
  // device has it off — the fix for the one state that gets a card-face
  // pill belongs at the top of the menu, not buried under everything else.
  if (row.deviceId && row.deviceType && !row.autoUpdate) {
    items.push({ key: 'auto-update-on', label: 'Turn auto-update back on', onClick: () => setAutoUpdate(true) });
  }

  if (row.tenantId) {
    items.push({
      key: 'open-business',
      label: 'Open business',
      onClick: () => { close(); navigate(`/tenant/${row.tenantId}`); },
    });
  }
  if (row.tenantId && row.locationId) {
    items.push({
      key: 'open-location',
      label: 'Open location',
      onClick: () => {
        close();
        navigate(`/tenant/${row.tenantId}?tab=locations&locationId=${encodeURIComponent(row.locationId!)}`);
      },
    });
  }
  if (row.orderId) {
    items.push({
      key: 'open-order',
      label: 'Open order',
      onClick: () => { close(); navigate(`/inventory/orders?open=${encodeURIComponent(row.orderId!)}`); },
    });
  }
  if (row.provisionCode) {
    items.push({ key: 'copy-code', label: 'Copy provisioning code', onClick: copyCode });
  }
  if (row.deviceId && row.deviceType && row.appVersion) {
    items.push({ key: 'pin-current', label: 'Pin to current version', onClick: pinToCurrent });
  }
  if (row.deviceId && row.deviceType && row.pinnedVersion) {
    items.push({ key: 'release-pin', label: 'Release pin', onClick: releasePin });
  }
  if (row.deviceId && row.deviceType) {
    // Contextual, per the approved mockup: only offer rings the device is
    // NOT already on.
    for (const ring of RINGS) {
      if (ring === row.releaseRing) continue;
      items.push({ key: `ring-${ring}`, label: `Move to ${ring}`, onClick: () => moveRing(ring) });
    }
  }
  if (row.deviceId && row.deviceType && row.autoUpdate) {
    items.push({ key: 'auto-update-off', label: 'Turn auto-update off', onClick: () => setAutoUpdate(false), caution: true });
  }
  if (row.deviceId) {
    items.push({
      key: 'reprovision',
      label: 'Re-provision',
      danger: true,
      onClick: () => { close(); onReprovision(); },
    });
  }

  return (
    <div className="relative inline-block" ref={rootRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={busy}
        title={buttonTitle}
        aria-label={buttonTitle}
        aria-haspopup="menu"
        aria-expanded={open}
        className={
          buttonClassName
          || 'inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl text-[15px] font-extrabold text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:cursor-not-allowed disabled:opacity-50'
        }
      >
        ⋯
      </button>
      {open && anchor && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label="More actions"
          style={{
            position: 'fixed',
            top: anchor.top,
            left: anchor.left,
            width: MENU_W,
            transform: anchor.flipUp ? 'translateY(-100%)' : undefined,
          }}
          className="z-[60] rounded-xl border border-gray-200 bg-white p-1 shadow-lg"
        >
          {items.length === 0 ? (
            <div className="px-3 py-2 text-[11.5px] text-gray-400">No actions available for this device</div>
          ) : (
            items.map((it) => (
              <button
                key={it.key}
                type="button"
                role="menuitem"
                onClick={it.onClick}
                disabled={busy}
                className={`flex min-h-[44px] w-full items-center gap-2 rounded-lg px-2.5 text-left text-[12px] font-bold disabled:cursor-not-allowed disabled:opacity-50 ${
                  it.danger
                    ? 'text-rose-600 hover:bg-rose-50'
                    : it.caution
                      ? 'text-amber-600 hover:bg-amber-50'
                      : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {it.label}
              </button>
            ))
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}
